'use client'

import React, { useState } from 'react'
import Image from 'next/image'

export function TestimonialsSection() {
  const testimonials = [
    {
      quote:
        "I didn't know what I didn't know. It's been so reassuring to have someone who can help us think of questions to ask and that can guide our preparation. They don't tell us what to do, just what to consider or to know more about.",
      attribution: 'Juliet S',
    },
    {
      quote:
        "Coming home with our newborn twins was exciting but we didn't have any idea how hard it would be. Thank you for being there for our family and making this time incredibly special for all of us.",
      attribution: 'John and David',
    },
    {
      quote:
        "Definitely worth the investment. We couldn't have made it through those first months without support.",
      attribution: 'Mary',
    },
  ]

  const [activeIndex, setActiveIndex] = useState(0)

  const goToPrevious = () => {
    setActiveIndex((prevIndex) => (prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1))
  }

  const goToNext = () => {
    setActiveIndex((prevIndex) => (prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1))
  }

  return (
    <section
      data-component-name="TestimonialsSection"
      style={{
        position: 'relative',
        padding: '120px 0',
        overflow: 'hidden',
      }}
    >
      {/* Background Image */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        <Image
          src="/images/testimonial-bg.jpg"
          alt=""
          fill
          style={{
            objectFit: 'cover',
          }}
        />
        {/* Overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
          }}
        />
      </div>

      {/* Decorative elements for desktop */}
      <div
        className="hidden lg:block"
        style={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '200px',
          height: '200px',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <Image
          src="/images/testimonial-left-img.svg"
          alt=""
          width={200}
          height={200}
          style={{
            width: '100%',
            height: 'auto',
          }}
        />
      </div>
      <div
        className="hidden lg:block"
        style={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '200px',
          height: '200px',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <Image
          src="/images/testimonial-right-img.svg"
          alt=""
          width={200}
          height={200}
          style={{
            width: '100%',
            height: 'auto',
          }}
        />
      </div>

      {/* Decorative element for mobile */}
      <div
        className="lg:hidden"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '300px',
          height: '300px',
          zIndex: 1,
          pointerEvents: 'none',
          opacity: 0.3,
        }}
      >
        <Image
          src="/images/testimonial-img-mob.svg"
          alt=""
          width={300}
          height={300}
          style={{
            width: '100%',
            height: 'auto',
          }}
        />
      </div>

      {/* Content */}
      <div
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"
        style={{ position: 'relative', zIndex: 2 }}
      >
        <div className="text-center">
          {/* Testimonial Quote */}
          <p
            style={{
              fontSize: '28px',
              fontFamily: 'var(--font-sulsans-light)',
              fontWeight: '400',
              color: '#071C26',
              lineHeight: '40px',
              marginBottom: '20px',
              minHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            &ldquo;{testimonials[activeIndex].quote}&rdquo;
          </p>

          {/* Attribution */}
          <p
            style={{
              fontSize: '18px',
              fontFamily: 'var(--font-copernicus)',
              fontWeight: '400',
              color: '#071C26',
              marginBottom: '40px',
            }}
          >
            {testimonials[activeIndex].attribution}
          </p>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-4">
            <button
              onClick={goToPrevious}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                border: '2px solid #3a4e3d',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3a4e3d'
                const svg = e.currentTarget.querySelector('svg')
                if (svg) (svg as SVGElement).style.stroke = '#ffffff'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                const svg = e.currentTarget.querySelector('svg')
                if (svg) (svg as SVGElement).style.stroke = '#3a4e3d'
              }}
              aria-label="Previous testimonial"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{ stroke: '#3a4e3d', transition: 'stroke 0.3s' }}
              >
                <path
                  d="M12 16L6 10L12 4"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              onClick={goToNext}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                border: '2px solid #3a4e3d',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3a4e3d'
                const svg = e.currentTarget.querySelector('svg')
                if (svg) (svg as SVGElement).style.stroke = '#ffffff'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                const svg = e.currentTarget.querySelector('svg')
                if (svg) (svg as SVGElement).style.stroke = '#3a4e3d'
              }}
              aria-label="Next testimonial"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{ stroke: '#3a4e3d', transition: 'stroke 0.3s' }}
              >
                <path
                  d="M8 4L14 10L8 16"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
